import AnimatedDash, {
  animationStyles,
} from "@components/buttons/common/AnimatedDash"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import ProductTag, {
  Container as ProductTagContainer,
} from "@components/ProductTag"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body16MainStyles,
  smallcapsRegular13Styles,
} from "@constants/typography"
import { getProductImageAlt } from "@utils/altText"
import { TabletAndDesktopOnly } from "@utils/MediaQuery"
import { getProductURL } from "@utils/urls"
import { Link } from "gatsby"
import _ from "lodash-es"
import React from "react"
import styled from "styled-components"

const GatsbyLink = styled(Link)<{ $to: string }>`
  ${({ $to }) => (!$to.length ? "pointer-events: none" : "cursor: pointer")};
  ${animationStyles}
`

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  position: relative;

  ${ProductTagContainer} {
    height: 32px;
    margin-left: 5.84px;
    margin-bottom: 4.32px;
  }
`

const Content = styled.div`
  width: 100%;
  padding-top: 14px;
`

const Row = styled.div`
  display: flex;
  margin-bottom: 4px;
  align-items: center;
  justify-content: space-between;
`

const ImageWrapper = styled.div`
  position: relative;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-end;
  display: flex;
  padding: 16px;
  transition: background-color ${animation.medium} ${cubicBezier};
  background-color: ${colors.backgroundGray};

  @media ${breakpoints.laptop} {
    align-self: stretch;
    justify-self: stretch;
    padding: 20px;

    &:hover {
      background-color: ${colors.backgroundGrayHover};
    }
  }

  ${ImageHelperContainer} {
    flex-grow: 0;
    flex-shrink: 0;
    width: 100%;
    height: auto;

    img {
      object-fit: contain;
    }
  }
`

const Title = styled.div`
  ${body16MainStyles}
`

const Price = styled.div`
  ${smallcapsRegular13Styles}
  opacity: 0.3;
`

const DashWrapper = styled.div`
  margin-right: 16px;
`

const Tags = styled.div`
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  display: flex;
  margin-top: auto;
`

const UByUAGTagWrapper = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  height: 32px;
  margin-bottom: 2px;
  padding-left: 7.5px;
  background-color: ${colors.brandMagenta};
  overflow: hidden;
`

export interface SearchBlockProps {
  imageUrl: string
  name: string
  onClick?: (e: any) => void
  price: string
  slug: string
  tags?: any[]
}

const SearchBlock: React.FC<SearchBlockProps> = ({
  imageUrl,
  name,
  onClick,
  price,
  slug,
  tags,
}: SearchBlockProps) => {
  const [uByUagTag, otherTags] = _.partition(
    tags,
    (tag) => tag?.text === "U by UAG"
  )

  return (
    <GatsbyLink
      $to={getProductURL(slug)}
      onClick={onClick}
      to={getProductURL(slug)}
    >
      <Container>
        <ImageWrapper>
          {(uByUagTag[0] || otherTags.length > 0) && (
            <Tags>
              {uByUagTag[0] && (
                <UByUAGTagWrapper>
                  <svg
                    fill="none"
                    height="12"
                    viewBox="0 0 14 12"
                    width="14"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect fill="#FFFEFD" height="12" width="14" />
                    <rect
                      fill="#9C3769"
                      height="8.61244"
                      width="2.20721"
                      x="2.26953"
                      y="1.66406"
                    />
                    <rect
                      fill="#9C3769"
                      height="8.61244"
                      width="2.20721"
                      x="9.52148"
                      y="1.66406"
                    />
                    <rect
                      fill="#9C3769"
                      height="9.45946"
                      transform="rotate(-90 2.26953 10.2773)"
                      width="2.00957"
                      x="2.26953"
                      y="10.2773"
                    />
                  </svg>
                  <ProductTag
                    backgroundColor={colors.brandMagenta.substr(1)}
                    key="uByUAGProduct"
                    text="U by UAG"
                  />
                </UByUAGTagWrapper>
              )}
              {otherTags &&
                otherTags.length > 0 &&
                otherTags.map((tag) => <ProductTag key={tag.text} {...tag} />)}
            </Tags>
          )}
          <ImageHelper
            alt={getProductImageAlt({
              imageType: "gallery",
              productName: name,
            })}
            src={imageUrl}
          />
        </ImageWrapper>
        <Content>
          <Row>
            <Title>{name}</Title>
            <TabletAndDesktopOnly>
              <DashWrapper>
                <AnimatedDash />
              </DashWrapper>
            </TabletAndDesktopOnly>
          </Row>
          <Price>{price}</Price>
        </Content>
      </Container>
    </GatsbyLink>
  )
}

export default SearchBlock
